// Le tabelle markdown, allineate.
//
// Una tabella scritta a mano ha le colonne storte, e finché resta storta la si
// rilegge male proprio mentre la si sta correggendo. pandoc non se ne accorge —
// il PDF viene identico — quindi questa è una cortesia verso chi SCRIVE, e per
// questo si applica al documento e non alla copia spedita al compositore.
//
// Si tocca solo ciò che è certamente una tabella: una riga di intestazione, una
// riga di separazione fatta di trattini, e le righe che seguono. I blocchi di
// codice recintati si saltano: lì dentro un `|---|` è testo, non struttura.

interface Colonna
{
    larghezza: number;
    allineamento: "sinistra" | "centro" | "destra" | "nessuno";
}

const SEPARAZIONE = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;
const RECINTO = /^\s*(```|~~~)/;


/** Le celle di una riga: il `|` scudato non separa niente. */
function celle(riga: string): string[]
{
    const senzaBordi = riga.trim().replace(/^\|/, "").replace(/\|$/, "");
    const pezzi: string[] = [];
    let corrente = "";

    for (let i = 0; i < senzaBordi.length; i++)
    {
        const ch = senzaBordi[i];

        if (ch === "\\" && senzaBordi[i + 1] === "|")
        {
            corrente += "\\|";
            i++;
            continue;
        }

        if (ch === "|") { pezzi.push(corrente.trim()); corrente = ""; continue; }

        corrente += ch;
    }

    pezzi.push(corrente.trim());
    return pezzi;
}


function allineamentoDi(cella: string): Colonna["allineamento"]
{
    const c = cella.trim();
    const sinistra = c.startsWith(":");
    const destra = c.endsWith(":");

    if (sinistra && destra) { return "centro"; }
    if (destra) { return "destra"; }
    if (sinistra) { return "sinistra"; }
    return "nessuno";
}


// La riga di separazione riempie di trattini anche i due spazi di cortesia
// delle righe di contenuto: `| A | B  |` sopra, `|---|----|` sotto. È la forma
// in cui il bordo della tabella si vede a colpo d'occhio nel sorgente.
function separatore(colonna: Colonna): string
{
    const dentro = colonna.larghezza + 2;

    switch (colonna.allineamento)
    {
        case "sinistra": return `:${"-".repeat(dentro - 1)}`;
        case "destra": return `${"-".repeat(dentro - 1)}:`;
        case "centro": return `:${"-".repeat(Math.max(1, dentro - 2))}:`;
        case "nessuno": return "-".repeat(dentro);
    }
}


function riempi(testo: string, larghezza: number, allineamento: Colonna["allineamento"]): string
{
    const spazio = Math.max(0, larghezza - [...testo].length);

    if (allineamento === "destra") { return " ".repeat(spazio) + testo; }

    if (allineamento === "centro")
    {
        const sinistra = Math.floor(spazio / 2);
        return " ".repeat(sinistra) + testo + " ".repeat(spazio - sinistra);
    }

    return testo + " ".repeat(spazio);
}


export interface Blocco
{
    /** Numero della prima riga del blocco (0-based). */
    daRiga: number;
    /** Numero della riga DOPO l'ultima del blocco. */
    aRiga: number;
    /** Il blocco già allineato. */
    testo: string;
}


/** Le tabelle del documento, ognuna con la sua forma canonica. */
export function blocchiTabella(testo: string): Blocco[]
{
    const righe = testo.split("\n");
    const blocchi: Blocco[] = [];
    let recinto: string | null = null;
    let i = 0;

    while (i < righe.length)
    {
        const riga = righe[i] ?? "";
        const apertura = RECINTO.exec(riga)?.[1];

        if (recinto !== null)
        {
            if (apertura === recinto) { recinto = null; }
            i++;
            continue;
        }

        if (apertura !== undefined) { recinto = apertura; i++; continue; }

        const dopo = righe[i + 1];
        const èTabella = riga.includes("|") && dopo !== undefined && SEPARAZIONE.test(dopo)
            && celle(riga).length === celle(dopo).length && celle(riga).length > 1;

        if (!èTabella) { i++; continue; }

        const corpo: string[][] = [];
        let j = i + 2;

        while (j < righe.length && (righe[j] ?? "").includes("|") && (righe[j] ?? "").trim() !== "")
        {
            corpo.push(celle(righe[j] ?? ""));
            j++;
        }

        blocchi.push({ daRiga: i, aRiga: j, testo: rendiTabella(riga, dopo, corpo) });
        i = j;
    }

    return blocchi;
}


function rendiTabella(intestazioneRiga: string, separazioneRiga: string, corpo: string[][]): string
{
    const intestazione = celle(intestazioneRiga);
    const colonne: Colonna[] = intestazione.map((testoCella, n) => ({
        larghezza: Math.max(
            [...testoCella].length,
            ...corpo.map((r) => [...(r[n] ?? "")].length),
            1,
        ),
        allineamento: allineamentoDi(celle(separazioneRiga)[n] ?? ""),
    }));

    const rendi = (valori: string[]): string =>
        `| ${colonne.map((c, n) => riempi(valori[n] ?? "", c.larghezza, c.allineamento)).join(" | ")} |`;

    // L'indentazione della tabella si conserva: dentro una lista una tabella
    // spostata a colonna zero esce dalla lista.
    const rientro = (/^\s*/.exec(intestazioneRiga)?.[0] ?? "");

    return [
        rientro + rendi(intestazione),
        `${rientro}|${colonne.map(separatore).join("|")}|`,
        ...corpo.map((r) => rientro + rendi(r)),
    ].join("\n");
}


export function canonicalizzaTabelle(testo: string): string
{
    const righe = testo.split("\n");

    // Dal fondo verso l'inizio: così gli indici delle tabelle precedenti
    // restano validi mentre si sostituisce.
    for (const blocco of [...blocchiTabella(testo)].reverse())
    {
        righe.splice(blocco.daRiga, blocco.aRiga - blocco.daRiga, ...blocco.testo.split("\n"));
    }

    return righe.join("\n");
}
